import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getBlogPost } from '../data/blogData';
import { ArrowLeft, Clock, Calendar, Twitter, Linkedin, Menu, X } from 'lucide-react';
import { Helmet } from 'react-helmet';

const blogContentStyles = `
  .blog-content p { color: #374151; line-height: 1.8; margin-bottom: 1.25rem; font-size: 1.0625rem; }
  .blog-content h2 { font-size: 1.5rem; font-weight: 700; color: #1a2744; margin-top: 2.5rem; margin-bottom: 1rem; }
  .blog-content h3 { font-size: 1.25rem; font-weight: 600; color: #1a2744; margin-top: 2rem; margin-bottom: 0.75rem; }
  .blog-content ul { margin: 1.25rem 0; padding-left: 0; list-style: none; }
  .blog-content ul li { color: #374151; margin-bottom: 0.75rem; padding-left: 1.5rem; position: relative; line-height: 1.7; }
  .blog-content ul li::before { content: "–"; position: absolute; left: 0; color: #B8956F; font-weight: 600; }
  .blog-content ol { margin: 1.25rem 0; padding-left: 1.5rem; }
  .blog-content ol li { color: #374151; margin-bottom: 0.75rem; line-height: 1.7; }
  .blog-content strong { font-weight: 600; color: #1a2744; }
  .blog-content a { color: #B8956F; text-decoration: none; }
  .blog-content a:hover { text-decoration: underline; }
  .blog-content blockquote { border-left: 3px solid #B8956F; padding-left: 1.25rem; margin: 1.5rem 0; font-style: italic; color: #4b5563; }
`;

const BlogPost = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const post = getBlogPost(slug);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-3" style={{ color: 'var(--navy-900)' }}>Article not found</h1>
          <button onClick={() => navigate('/blog')} className="font-medium" style={{ color: 'var(--cream-600)' }}>
            ← Back to blog
          </button>
        </div>
      </div>
    );
  }

  const shareUrl = `https://bragbox.ai/blog/${post.slug}`;

  // Build JSON-LD structured data: Article (+ optional Product + FAQ) for SEO.
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.excerpt,
    datePublished: post.date,
    author: { '@type': 'Organization', name: 'BragBox' },
    publisher: {
      '@type': 'Organization',
      name: 'BragBox',
      logo: { '@type': 'ImageObject', url: 'https://bragbox.ai/assets/bragbox-hero.png' }
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': shareUrl },
    url: shareUrl
  };

  const faqSchema = post.structuredData?.faqs?.length
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: post.structuredData.faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a }
        }))
      }
    : null;

  const productSchema = post.structuredData?.product
    ? {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: post.structuredData.product.name,
        description: post.structuredData.product.description,
        brand: { '@type': 'Brand', name: post.structuredData.product.brand || 'BragBox' },
        offers: post.structuredData.product.offers
          ? {
              '@type': 'Offer',
              price: post.structuredData.product.offers.price,
              priceCurrency: post.structuredData.product.offers.currency,
              availability: 'https://schema.org/InStock',
              url: 'https://bragbox.ai/pricing'
            }
          : undefined
      }
    : null;

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{`${post.title} · BragBox`}</title>
        <meta name="description" content={post.excerpt} />
        <link rel="canonical" href={shareUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:url" content={shareUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.excerpt} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        {faqSchema && (
          <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        )}
        {productSchema && (
          <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
        )}
      </Helmet>
      <style>{blogContentStyles}</style>

      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex justify-between items-center">
          <button onClick={() => navigate('/')} className="flex items-center gap-3">
            <img src="/assets/bragbox-hero.png" alt="BragBox Logo" className="h-10 md:h-12" />
          </button>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-2">
            <button onClick={() => navigate('/blog')} className="px-3 py-2 rounded-lg font-medium text-sm transition-colors hover:bg-gray-50" style={{ color: 'var(--navy-900)' }}>Blog</button>
            <button onClick={() => navigate('/pricing')} className="px-3 py-2 rounded-lg font-medium text-sm transition-colors hover:bg-gray-50" style={{ color: 'var(--navy-900)' }}>Pricing</button>
            <button onClick={() => navigate('/login?existing=true')} className="px-3 py-2 rounded-lg font-medium text-sm transition-colors" style={{ color: 'var(--navy-900)' }}>Log In</button>
            <button
              onClick={() => navigate('/login?signup=true')}
              className="px-5 py-2.5 rounded-lg font-semibold text-sm transition-all shadow-md hover:shadow-lg inline-flex items-center"
              style={{ backgroundColor: 'var(--navy-900)', color: 'white' }}
            >
              Start free
            </button>
          </div>

          {/* Mobile: CTA + Hamburger */}
          <div className="flex md:hidden items-center gap-2">
            <button
              onClick={() => navigate('/login?signup=true')}
              className="px-4 py-2 rounded-lg font-semibold text-sm inline-flex items-center"
              style={{ backgroundColor: 'var(--navy-900)', color: 'white' }}
            >
              Start free
            </button>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 rounded-lg" style={{ color: 'var(--navy-900)' }}>
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-4 py-3 flex flex-col gap-1">
            <button onClick={() => { navigate('/blog'); setMobileMenuOpen(false); }} className="text-left px-3 py-3 rounded-lg font-medium text-sm hover:bg-gray-50" style={{ color: 'var(--navy-900)' }}>Blog</button>
            <button onClick={() => { navigate('/pricing'); setMobileMenuOpen(false); }} className="text-left px-3 py-3 rounded-lg font-medium text-sm hover:bg-gray-50" style={{ color: 'var(--navy-900)' }}>Pricing</button>
            <button onClick={() => { navigate('/login?existing=true'); setMobileMenuOpen(false); }} className="text-left px-3 py-3 rounded-lg font-medium text-sm hover:bg-gray-50" style={{ color: 'var(--navy-900)' }}>Log In</button>
          </div>
        )}
      </header>

      {/* Article */}
      <main className="max-w-3xl mx-auto px-4 md:px-8 py-12">
        {/* Back link */}
        <button
          onClick={() => navigate('/blog')}
          className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-900 text-sm mb-8 transition-colors"
        >
          <ArrowLeft size={16} />
          Back to blog
        </button>

        {/* Post header */}
        <div className="mb-10">
          <h1 className="text-3xl md:text-4xl font-bold leading-tight mb-4" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
            {post.title}
          </h1>
          <div className="flex items-center gap-5 text-sm text-gray-500">
            <span className="flex items-center gap-1.5">
              <Calendar size={14} />
              {post.date}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock size={14} />
              {post.readTime}
            </span>
          </div>
        </div>

        {/* Post body */}
        <div
          className="blog-content"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        {/* Share */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <p className="text-sm font-medium text-gray-700 mb-3">Share this article</p>
          <div className="flex gap-3">
            <a
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-gray-600 border border-gray-200 rounded-lg px-4 py-2 hover:border-gray-400 transition-colors"
            >
              <Twitter size={14} /> Share on X
            </a>
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-gray-600 border border-gray-200 rounded-lg px-4 py-2 hover:border-gray-400 transition-colors"
            >
              <Linkedin size={14} /> Share on LinkedIn
            </a>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 rounded-2xl p-8 text-center" style={{ backgroundColor: 'var(--cream-100)', border: '1px solid var(--cream-300)' }}>
          <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>Leave this article with the next step picked.</h3>
          <p className="text-gray-600 mb-5 text-sm">BragBox captures your wins as they happen and polishes them into review-ready language. Free to start, no credit card at signup.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate('/login?signup=true')}
              className="font-semibold px-6 py-3 rounded-lg transition-all shadow-md hover:shadow-lg inline-flex items-center justify-center"
              style={{ backgroundColor: 'var(--navy-900)', color: 'white' }}
            >
              Start free
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-4">Already have an account? <button onClick={() => navigate('/login')} className="underline">Log in</button>.</p>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 px-4 md:px-8 border-t border-gray-200 mt-16">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <img src="/assets/bragbox-hero.png" alt="BragBox Logo" className="h-8" />
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <button onClick={() => navigate('/blog')} className="hover:text-gray-700">Blog</button>
              <button onClick={() => navigate('/pricing')} className="hover:text-gray-700">Pricing</button>
              <button onClick={() => navigate('/contact')} className="hover:text-gray-700">Contact</button>
              <a href="https://www.linkedin.com/company/bragboxai/" target="_blank" rel="noopener noreferrer" className="hover:text-gray-700">LinkedIn</a>
              <a href="https://x.com/bragbox_ai" target="_blank" rel="noopener noreferrer" className="hover:text-gray-700">X</a>
            </div>
          </div>
          <div className="text-center mt-8">
            <p className="text-gray-400 text-sm">© 2026 BragBox. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogPost;
